import { useLocation } from "wouter";
import { useContext } from "react";
import intl from "react-intl-universal";
import { EntryContext } from "../../contexts/EntryContext";
import { ValidationContext } from "../../contexts/ValidationContext";
import commonUtils from "../../../script/utils/commonUtils";
import entryRest from "../../../rest/entryRest";
import planRest from "../../../rest/planRest";
import ixusRest from "../../../rest/ixusRest";

export const useSaveEntry = () => {
    const entryContext = useContext(EntryContext);
    const {saving, setSaving} = entryContext;
    const validationContext = useContext(ValidationContext);
    const [location, setLocation] = useLocation();

    const saveEntry = async entry => {
        if(saving) {
            return;
        }
        setSaving(true);
        try {
            // validation
            const allValid = await validationContext.allValid();
            validationContext.setErrorsVisible(true);
            // the validation context only knows inputs currently shown, e.g. area names only while the areas layer is open
            const areasValid = (entry.calcSetting?.calcAreas ?? []).every(calcArea => !commonUtils.isBlank(calcArea.name));
            if(!allValid || !areasValid) {
                const globalErrorMessages = validationContext.getInvalidGroups();
                const areasErrorMessage = intl.get("entry.plan.areas.title");
                if (!areasValid && !globalErrorMessages.includes(areasErrorMessage)) {
                    globalErrorMessages.push(areasErrorMessage);
                }
                commonUtils.dispatchEvent("exception", {
                    i18nLabel: "validateEntry",
                    args: { errorLocations: globalErrorMessages.join(", ") },
                });
                return;
            }

            let persisted;
            try {
            // prevent images from disapearing in case of failure
                const request = structuredClone(entry);

                //remove plan images to reduce request size
                const plans = [];
                for (const plan of request.plans) {
                    plans.push(Object.assign({}, plan));
                    delete plan.image;
                }
                // remove ixus report image to reduce request size
                const ixusReportImage = request.ixusSetting?.reportImage;
                if (ixusReportImage) {
                    //remove ixus report image to reduce request size
                    delete request.ixusSetting.reportImage;
                }
                //inserted entry contains generated ids
                if (request.id === null) {
                    persisted = await entryRest.insert(request);
                    setLocation(location.replace("null", persisted));
                } else {
                    persisted = await entryRest.update(request);
                }
                //add old images to new entry. In case image saving fails, it will allow another attempt to save.
                for (const plan of plans) {
                    const persistedPlan = persisted.plans.find(p => p.key === plan.key);
                    if (plan.image && persistedPlan) {
                        persistedPlan.image = plan.image;
                    }
                }
                if(ixusReportImage) {
                    persisted.ixusSetting.reportImage = ixusReportImage;
                }
                //persist removed plan images
                const promises = [];
                for (const plan of persisted.plans) {
                    const found = plans.find(obj => obj.key === plan.key);
                    if (found && found.image) {
                        promises.push(planRest.setImageById(plan.id, found.image));
                    }
                }
                // persist removed ixus report image
                if(ixusReportImage) {
                    promises.push(ixusRest.setReportImage(persisted.id, ixusReportImage));
                }
                await Promise.all(promises);
                entryContext.setEntry(persisted);
            } catch (e) {
                if (persisted) {
                    entryContext.setEntry(persisted);
                }
                commonUtils.dispatchEvent("exception", { i18nLabel: "validateEntry" });
                throw e;
            }
        } finally {
            setSaving(false);
        }
    };

    return {saveEntry, saving};
};